import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faHammer, faWrench, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import repairImage from '../assets/rep.jpg';
import Card from '../components/common/Card';

interface Service {
  id: number;
  title: string;
  shortDescription: string;
  fullDescription: string;
  icon: any;
  image: string;
  features: string[];
}

const services: Service[] = [
  {
    id: 1,
    title: 'Travaux neuves',
    shortDescription: 'Réalisation de projets résidentiels et commerciaux avec les dernières normes de construction.',
    fullDescription: 'Notre équipe d\'experts vous accompagne dans la réalisation de votre projet de construction neuve, de la conception à la livraison. Nous mettons notre savoir-faire au service de vos ambitions pour créer des espaces fonctionnels, esthétiques et durables qui répondent parfaitement à vos besoins.',
    icon: faBuilding,
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop',
    features: [
      'Maisons individuelles',
      'Immeubles résidentiels',
      'Bâtiments commerciaux',
      'Bureaux et espaces de travail',
      'Matériaux écologiques et durables'
    ]
  },
  {
    id: 2,
    title: 'Rénovation',
    shortDescription: 'Transformation et modernisation de vos espaces existants pour une nouvelle vie.',
    fullDescription: 'Donnez une seconde vie à votre bien immobilier grâce à nos services de rénovation. Que vous souhaitiez moderniser votre intérieur, optimiser l\'espace ou améliorer les performances énergétiques de votre bâtiment, notre équipe vous propose des solutions adaptées à votre budget et à vos attentes.',
    icon: faHammer,
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=2071&auto=format&fit=crop',
    features: [
      'Rénovation complète',
      'Rénovation partielle',
      'Rénovation énergétique',
      'Mise aux normes',
      'Rénovation d\'appartements',
      'Rénovation de maisons anciennes'
    ]
  },
  {
    id: 3,
    title: 'Réparation et maintenance',
    shortDescription: 'Entretien et réparation de vos installations pour garantir leur longévité et leur performance.',
    fullDescription: 'Préservez la valeur et la fonctionnalité de votre bien immobilier grâce à nos services de réparation et de maintenance. Notre équipe de professionnels intervient rapidement pour diagnostiquer et résoudre tous types de problèmes, des plus simples aux plus complexes, pour vous garantir tranquillité d\'esprit et confort au quotidien.',
    icon: faWrench,
    image: repairImage,
    features: [
      'Diagnostic et inspection',
      'Réparations urgentes',
      'Maintenance préventive',
      'Contrats d\'entretien',
      'Suivi et garantie des travaux'
    ]
  }
];

const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Fonction pour ouvrir la modal de service
  const openServiceModal = (service: Service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  // Fonction pour fermer la modal de service
  const closeServiceModal = () => {
    setIsModalOpen(false);
    // Petit délai avant de réinitialiser le service sélectionné pour permettre l'animation de sortie
    setTimeout(() => {
      setSelectedService(null);
    }, 300);
  };

  // Fonction pour gérer le clic sur le bouton "Demander un devis"
  const handleQuoteRequest = (service: Service) => {
    // Fermer la modal avant la redirection
    closeServiceModal();
    // Rediriger vers la page de contact avec les paramètres du service
    window.location.href = `/#/contact?service=${encodeURIComponent(service.title)}&type=${service.id}`;
  };

  // Empêcher la propagation des clics
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Gérer la touche Echap pour fermer la modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeServiceModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  // Gérer le défilement du body
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isModalOpen]);

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative bg-primary-dark py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary-light opacity-90" data-component-name="Services"></div>
        </div>
        <div className="relative container mx-auto px-4 md:px-6 z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
              Nos Services
            </h1>
            <p className="font-heading text-xl mb-8">
              Des solutions complètes et sur mesure pour tous vos projets de construction et de rénovation
            </p>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Découvrez nos prestations
            </h2>
            <p className="font-heading text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Nous proposons une gamme complète de services pour répondre à tous vos besoins en matière de construction, rénovation et aménagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card
                key={service.id}
                image={service.image}
                title={service.title}
                description={service.shortDescription}
                icon={<FontAwesomeIcon icon={service.icon} className="text-primary text-xl" />}
                onClick={() => openServiceModal(service)}
                className="cursor-pointer"
              />
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-100 dark:bg-gray-800 py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins et obtenir un devis personnalisé.
            </p>
            <a 
              href="/#/contact" 
              className="inline-block px-8 py-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors duration-300"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      <AnimatePresence>
        {selectedService && isModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm"
            onClick={closeServiceModal}
          >
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-2xl"
              onClick={handleContentClick}
            >
              {/* Header avec bouton de fermeture */}
              <div className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center p-4 bg-gradient-to-b from-black/70 to-transparent">
                <h2 className="text-2xl font-bold text-white drop-shadow-md flex items-center">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={selectedService.icon} className="text-white" />
                  </div>
                  {selectedService.title}
                </h2>
                <button 
                  onClick={closeServiceModal}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                  aria-label="Fermer"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Contenu en deux colonnes sur grand écran */}
              <div className="flex flex-col md:flex-row h-[90vh]">
                {/* Image du service (colonne gauche) */}
                <div className="relative w-full md:w-1/2 h-64 md:h-full">
                  <img 
                    src={selectedService.image} 
                    alt={selectedService.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                </div>
                
                {/* Détails du service (colonne droite) */}
                <div className="w-full md:w-1/2 p-6 overflow-y-auto">
                  {/* Description */}
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                      À propos de ce service
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-6">
                      {selectedService.fullDescription}
                    </p>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-5 border-l-4 border-primary">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                        Ce que nous proposons
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedService.features.map((feature, index) => (
                          <div key={index} className="flex items-center">
                            <div className="w-6 h-6 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center mr-2">
                              <FontAwesomeIcon icon={faCheck} className="text-primary text-xs" />
                            </div>
                            <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Boutons d'action */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-8">
                    <button 
                      onClick={() => handleQuoteRequest(selectedService)}
                      className="flex-1 px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors duration-300"
                    >
                      Demander un devis
                    </button>
                    <a 
                      href="/#/contact"
                      className="flex-1 px-6 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 font-medium rounded-md transition-colors duration-300 text-center"
                    >
                      Nous contacter
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Services;
